import React, { Component, Fragment } from 'react';
import classNames from 'classnames';
import { injectIntl, WrappedComponentProps } from 'react-intl';

import { ProductCard, PesCcMain } from '@pes-ui/components-react';
import { withNotificationStorageContext } from '../../hocs/NotificationStorage';
import Cell from './Cell';
import styles from './index.module.scss';
import OptionsCard from '../OptionsCard';
import { DesktopCompareResults } from './DesktopCompareResults';
import { isCategorySectionAvailable, goToCartUrl, isHostnameContainsCC, getLocaleInfo } from '../../utils';

import { EStatus, CircularProductTypes } from '@pes-ui/utils/lib/constants';

import { ProductsApi } from '@pes-ui/utils/lib/types/api/products-api';
import { ProductsParametersApi } from '@pes-ui/utils/lib/types/api/products-parameters-api';
import { ProductInfoApi } from '@pes-ui/utils/lib/types/api/products-info-api';
import { MappedProduct } from '../../utils/mappers';
import { CommerceConnectorApi } from '@pes-ui/utils/lib/types/api/commerce-connector-api';
import { getProductCtas } from '../../utils/productCtas';
import { BaseRouteProps } from '@pes-ui/utils/lib/types/route-props';
import { TProductsRatings } from '@pes-ui/components/dist/collection/globals/types';
import { ProductAvailabilityApi } from '@pes-ui/utils/lib/types/api/product-availability-api';
import { ApcCtaApi } from '@pes-ui/utils/lib/types/api/apc-cta-api';
import { getAPCDistributor, getSingleDistributor } from '@pes-ui/components/dist/collection/components/commerce-connector/utils';
import { ProductsDistributorsInfo } from '@pes-ui/components/dist/collection/components/commerce-connector/types';
import {
  fetchers,
} from '@pes-ui/utils';
import { ComparatorRuntimeInfo } from '@pes-ui/utils/lib/types/comparator';

export const DesktopViewContext = React.createContext(null);

type State = {
    ccProductIdToShow: string;
    ccBuyOnlineAvailability: Record<string, boolean>;
    ccPricesMap: Record<string, string>;
    isStuck: boolean;
    isHorizontalScroll: boolean;
};

type Props = BaseRouteProps & {
    toggleSectionHandler: (currentStateKey) => void;
    toggleSectionState: Record<string, boolean>;
    products: MappedProduct[];
    productsUIAAS: ProductsApi.Products;
    productsUIAASinfo: ProductInfoApi.GetProductsInfoResponse['productAdditionalInfos'];
    commerceConnector: CommerceConnectorApi.GetCommerceConnectorResponse;
    myProducts: string[];
    labels: ProductsApi.Labels;
    downloadButtonLabels: ProductInfoApi.DownloadButtonLabels;
    categories: ProductsParametersApi.Sections;
    onRemove: (arg) => void;
    onUpdateMyProductsList: () => void;
    showDevPrioritiesInfo: boolean;
    keyCategories: ProductsParametersApi.Sections;
    handleHideSimilarChange: () => void;
    hideSimilarFeatures: boolean;
    highlightDifferencesChange: () => void;
    highlightDifferences: boolean;
    hideSimilaritiesCalculated?: boolean;
    productsRatings?: TProductsRatings;
    bazaarVoiceEnabled?: boolean;
    isAPCSite?: boolean;
    apcCta?: ApcCtaApi.ApcCtaResponse;
    productAvailabilityInfo?:ProductAvailabilityApi.ProductAvailabilityResponse;
    runtimeInfo?: ComparatorRuntimeInfo;
    match: {
        params: {
            compareSessionId: string;
            skus?: string;
            runtime?: string;
            cc: string;
            lc: string;
        },
    };
    notification: {
        stack: [];
        addNotification: (params: {
            status: EStatus;
            message: React.ReactNode;
            buttonText?: string;
            countryCode?: string;
            languageCode?: string;
        }) => void;
        removeNotification: () => void;
    };
    linkClickHandlerCallback?: (link: string) => void;
} & WrappedComponentProps;
class DesktopProducts extends Component<Props, State> {
    static defaultProps = {
        showDevPrioritiesInfo: false,
        keyCategories: [],
    }

    private intersectionObserver: IntersectionObserver;
    private resizeObserver: ResizeObserver;
    private productsContainer: React.RefObject<HTMLDivElement>;
    private minifiedProductsContainer: React.RefObject<HTMLDivElement>;
    private stickyContainer: React.RefObject<HTMLDivElement>;
    private compareResultsContainer: React.RefObject<HTMLDivElement>;
    private scroller: React.RefObject<HTMLDivElement>;
    private scrollerContent: React.RefObject<HTMLDivElement>;
    private additionalDistributors: ProductsDistributorsInfo | null = null;

    constructor(props) {
        super(props);
        this.state = {
            ccProductIdToShow: null,
            ccBuyOnlineAvailability: {},
            ccPricesMap: null,
            isStuck: false,
            isHorizontalScroll: false
        };
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
            this.setState({ isStuck: entry.boundingClientRect.y < entry.rootBounds.y});
        }, {threshold: [1]});
        this.resizeObserver = new ResizeObserver(([entry]) => {
            const scrollableAreaWidth = entry.target.scrollWidth;
            this.scrollerContent.current.style.width = `${scrollableAreaWidth}px`;
            const minifiedProductsHeight = entry.contentRect.height;
            this.stickyContainer.current.style.marginTop = `-${minifiedProductsHeight}px`;
        });

        this.productsContainer = React.createRef<HTMLDivElement>();
        this.minifiedProductsContainer = React.createRef<HTMLDivElement>();
        this.stickyContainer = React.createRef<HTMLDivElement>();
        this.compareResultsContainer = React.createRef<HTMLDivElement>();
        this.scroller = React.createRef<HTMLDivElement>();
        this.scrollerContent = React.createRef<HTMLDivElement>();
    }

    componentDidMount() {
        this.intersectionObserver.observe(this.minifiedProductsContainer.current);
        this.resizeObserver.observe(this.minifiedProductsContainer.current);
        this.productsContainer.current.addEventListener('scroll', this.syncScroll);
        this.minifiedProductsContainer.current.addEventListener('scroll', this.syncScroll);
        this.compareResultsContainer.current.addEventListener('scroll', this.syncScroll);
        this.scroller.current.addEventListener('scroll', this.syncScroll);
    }

    componentWillUnmount() {
        this.intersectionObserver.unobserve(this.minifiedProductsContainer.current);
        this.resizeObserver.unobserve(this.minifiedProductsContainer.current);
        this.productsContainer.current.removeEventListener('scroll', this.syncScroll);
        this.minifiedProductsContainer.current.removeEventListener('scroll', this.syncScroll);
        this.compareResultsContainer.current.removeEventListener('scroll', this.syncScroll);
        this.scroller.current.removeEventListener('scroll', this.syncScroll);
    }

    apcCtaButtonClick = event => {

        const {
            countryCode,
            languageCode,
        } = this.props;
        const { agentChatCscId } = this.props.commerceConnector.options;

        const { productId, bundle: { bundleId, qty} } = event.detail;
        const hostUrl = this.props?.apcCta?.urls?.apcHost;
        if (hostUrl) {
            const cartPageUrl = goToCartUrl(hostUrl, countryCode, languageCode, productId, bundleId, qty, agentChatCscId);
            window.open(cartPageUrl, '_self');
        }
    };

    syncScroll = (event) => {
        this.productsContainer.current.scrollLeft = event.target.scrollLeft;
        this.minifiedProductsContainer.current.scrollLeft = event.target.scrollLeft;
        this.compareResultsContainer.current.scrollLeft = event.target.scrollLeft;
        this.scroller.current.scrollLeft = event.target.scrollLeft;
        this.setState({ isHorizontalScroll: event.target.scrollLeft !== 0});
    };

    handleCtaClick = async (event) => {
        event.stopPropagation();

        const {
            detail: {
                type,
                product,
            }
        } = event;

        const {
            notification,
            contextRoot,
            countryCode,
            languageCode,
            myProducts,
            labels,
            onUpdateMyProductsList,
        } = this.props;

       const myProductsPayload={
            options: {
                allowPartialAdding: false
            },
            products: [{ productId: product.id, quantity: 1 }]
        }

        try {
            switch (type) {
                case 'cart':
                    await fetchers.postMyProducts({
                        contextRoot,
                        countryCode: !isHostnameContainsCC() ? countryCode : null,
                        languageCode,
                        payload: myProductsPayload
                    });

                    onUpdateMyProductsList();

                    notification.addNotification({
                        status: EStatus.WARNING,
                        message: this.props.intl.formatMessage({
                            id: 'labels.addedToCartLabel',
                            defaultMessage: labels.addedToCartLabel,
                        }),
                        buttonText: this.props.intl.formatMessage({
                            id: 'labels.viewCartLabel',
                            defaultMessage: labels.viewCartLabel
                        }),
                        countryCode,
                        languageCode,
                    });
                    break;
                case 'favorites':
                    if (myProducts?.includes(product.id)) {
                        await fetchers.deleteMyProducts({
                            contextRoot,
                            countryCode: !isHostnameContainsCC() ? countryCode : null,
                            languageCode,
                            params: { id: product.id }
                        });

                        onUpdateMyProductsList();

                        notification.addNotification({
                            status: EStatus.WARNING,
                            message: this.props.intl.formatMessage({
                                id: 'labels.removedFromMyProductsLabel',
                                defaultMessage: labels.removedFromMyProductsLabel
                            }),
                            buttonText: '',
                        });
                    } else {
                        await fetchers.postMyProducts({
                            contextRoot,
                            countryCode: !isHostnameContainsCC() ? countryCode : null,
                            languageCode,
                            payload: myProductsPayload
                        });

                        onUpdateMyProductsList();

                        notification.addNotification({
                            status: EStatus.WARNING,
                            message: this.props.intl.formatMessage({
                                id: 'labels.addedToMyProductsLabel',
                                defaultMessage: labels.addedToMyProductsLabel
                            }),
                            buttonText: this.props.intl.formatMessage({
                                id: 'labels.viewMyProductsLabel',
                                defaultMessage: labels.viewMyProductsLabel
                            }),
                            countryCode,
                            languageCode,
                        });
                    }
                    break;
                default:
                    break;
            }
        } catch (error) {
            console.error(error);

            notification.addNotification({
                status: EStatus.ERROR,
                message: this.props.intl.formatMessage({
                    id:' comparison.errorMessage',
                    defaultMessage: 'You just added item to your Products'
                }),
                buttonText: '',
            });
        }
    };

    onProductBuyOnlineClick = (el) => {
        const { productsUIAAS } = this.props;
        const ccProductToShow = productsUIAAS.find(
            (product) => product.commercialReference === el.detail.repositoryId,
        );

        this.setState({ ccProductIdToShow: ccProductToShow.repositoryId });
    }

    handleRemove = (event: CustomEvent) => {
        const { repositoryId } = event.detail;
        if (event.detail.bundle) {
            const { quantity, repositoryId: bundleId } = event.detail.bundle;
            this.props.onRemove(`${repositoryId}:(${quantity})${bundleId}`);
        } else {
            this.props.onRemove(repositoryId);
        }

    };

    handleCcDialogClosed = () => {
        this.setState({ ccProductIdToShow: null });
    };

    handleCcBtnBuyOnlineAvailability = (event: CustomEvent<Record<string, boolean>>) => {
        this.setState({ ccBuyOnlineAvailability: event.detail });
    };

    handleCcBroadcastPrices = (event: CustomEvent<{
        prices: Record<string, string>;
        priceFromLabel?: string;
    }>) => {
        this.setState({ ccPricesMap: event.detail?.prices });
    };

    getPartnerPrice = (product: ProductsApi.Product): string => {
        if (this.state.ccPricesMap && product.repositoryId in this.state.ccPricesMap) {
            return this.state?.ccPricesMap[product.repositoryId];
        }
    };

    isShowBuyOnline = (product): boolean => {
        return !!this.state.ccBuyOnlineAvailability[product.repositoryId] ||
            (!this.props.isAPCSite && this.additionalDistributors && !!this.additionalDistributors[product.repositoryId]);
    }

    renderProductCard = (isMinified: boolean) => {
        const {
            productsRatings,
            productsUIAAS,
            productsUIAASinfo,
            labels,
            contextRoot,
            countryCode,
            languageCode,
            commerceConnector,
            myProducts,
            bazaarVoiceEnabled,
            isAPCSite,
            apcCta,
            productAvailabilityInfo,
            downloadButtonLabels,
        } = this.props;

        const { downloadButtonLabel, viewOnlineButtonLabel, seeSoftwareButtonLabel } = downloadButtonLabels;
        const productsForCC = productsUIAAS
          .filter(({ circularType }) => {
            if (!circularType) {
              return true;
            }

            return circularType === CircularProductTypes.LINEAR;
          })
          .map(
            ({ commercialReference, description, pictureUrl: imgUrl, pictureAltText: imgAlt }) => ({
                commercialReference,
                description,
                imgUrl,
                imgAlt,
            }),
        );

        const HOST = process.env.API_HOST || `${window.location.protocol}//${window.location.host}`;
        const HOST_WITH_LOCALE = process.env.API_HOST
            ?
                isHostnameContainsCC()
                    ? `${process.env.API_HOST}${contextRoot}/${languageCode}`
                    : `${process.env.API_HOST}${contextRoot}/${countryCode}/${languageCode}`

            :
                isHostnameContainsCC()
                    ? `${HOST}${contextRoot}/${languageCode}`
                    : `${HOST}${contextRoot}/${countryCode}/${languageCode}`;

        const availableProductsUIAAS = productsUIAAS
            && productsUIAASinfo
            && productsUIAASinfo.length > 0
            && productsUIAAS.length > 0
            && productsUIAAS;

        const supplementedLabels = {
            ...labels,
            partnerPriceLabel: commerceConnector?.labels?.priceFrom
        };

        const localeCode = getLocaleInfo(countryCode, languageCode);

        return (
            <Fragment>
                <PesCcMain
                    host={HOST_WITH_LOCALE}
                    products={productsForCC}
                    productIdToShow={this.state.ccProductIdToShow}
                    onCcDialogClosed={this.handleCcDialogClosed}
                    onCcBtnBuyOnlineAvailability={this.handleCcBtnBuyOnlineAvailability}
                    onCcBroadcastPrices={this.handleCcBroadcastPrices}
                    additionalDistributors={!this.props.isAPCSite && this.additionalDistributors}
                    style={{width:'0px'}}
                />
                { Boolean(availableProductsUIAAS) && availableProductsUIAAS
                        .map((product, index) => {
                            if(productsUIAASinfo[index].apcURL) {
                                if (!this.additionalDistributors) {
                                    this.additionalDistributors = {};
                                }
                                const { apcURL } = productsUIAASinfo[index];
                                this.additionalDistributors[product.repositoryId] =
                                    [ getAPCDistributor(apcURL, labels.purchaseOnAPCLabel), ];
                            }
                            if (product?.bundle) {
                                product.commercialReference = product.repositoryId;
                            }
                            return (
                                <div
                                    className={classNames(styles.cell, {
                                        [styles['noBorder']]: isMinified,
                                    })}
                                    key={ `${product.repositoryId}+${index}` }
                                >
                                    <div className={styles.productCardWrapper}>
                                        <ProductCard
                                            style={{ height: '100%' }}
                                            hostWithoutLocaleCodes={HOST}
                                            className={styles.productCard}
                                            product={product}
                                            info={productsUIAASinfo[index]}
                                            labels={supplementedLabels}
                                            downloadButtonLabel={downloadButtonLabel}
                                            viewOnlineButtonLabel={viewOnlineButtonLabel}
                                            seeSoftwareButtonLabel={seeSoftwareButtonLabel}
                                            isGridView={true}
                                            isMinified={isMinified}
                                            showBuyOnline={!isMinified && this.isShowBuyOnline(product)}
                                            singleDistributor={
                                                !this.state.ccBuyOnlineAvailability[product.repositoryId]
                                                    ? getSingleDistributor(this.additionalDistributors, product.repositoryId)
                                                    : null
                                            }
                                            width='available'
                                            isFocusShadowEnabled={false}
                                            withBorder={false}
                                            showHighlightedChar={true}
                                            showClose={true}
                                            showDocuments={false}
                                            onApcCtaButtonDidClick={this.apcCtaButtonClick}
                                            onCloseClick={this.handleRemove}
                                            onBuyOnlineClick={this.onProductBuyOnlineClick}
                                            partnerPrice={this.getPartnerPrice(product)}
                                            productCtas={getProductCtas({
                                                labels: supplementedLabels,
                                                commerceConnector,
                                                myProducts,
                                            })}
                                            onCtaClick={this.handleCtaClick}
                                            bazaarVoiceEnabled={bazaarVoiceEnabled}
                                            productsRatings={productsRatings}
                                            isAPCSite={isAPCSite}
                                            apcCta={apcCta}
                                            availabilityInfo={productAvailabilityInfo}
                                            productId={product.repositoryId}
                                            languageCode={languageCode}
                                            countryCode={countryCode}
                                            localeCode={localeCode}
                                        />
                                    </div>
                                </div>
                            );
                        })
                }
            </Fragment>
        );
    };

    renderProducts(isMinified: boolean) {
        const {
            products,
            categories,
            keyCategories,
            highlightDifferencesChange,
            highlightDifferences,
            handleHideSimilarChange,
            hideSimilarFeatures,
        } = this.props;

        const categoryList = [keyCategories, categories];
        return (
            <div
                className={classNames({
                    [styles.stuckProductsWrapper]: this.state.isStuck,
                    [styles.stickyProducts]: isMinified,
                })}
            >
                <div
                    ref={isMinified ? this.minifiedProductsContainer : this.productsContainer}
                    className={classNames(styles.products, {
                        [styles.stuckProducts]: this.state.isStuck,
                        [styles.products__minified]: isMinified
                    })}
                >
                    <Cell
                        labelCol
                        isMinimized={true}
                        isStuck={this.state.isStuck}
                        isHorizontalScroll={this.state.isHorizontalScroll}
                    >
                        <OptionsCard
                            products={products}
                            categoryList={categoryList}
                            highlightDifferencesChange={highlightDifferencesChange}
                            highlightDifferences={highlightDifferences}
                            handleHideSimilarChange={handleHideSimilarChange}
                            hideSimilarFeatures={hideSimilarFeatures}
                            isMinified={isMinified}
                        />
                    </Cell>
                    {this.renderProductCard(isMinified)}
                </div>
            </div>
        );
    }

    renderCompareResults = (category: ProductsParametersApi.Sections) => {
        const {
            products,
            toggleSectionHandler,
            toggleSectionState,
            highlightDifferences,
            hideSimilarFeatures,
            showDevPrioritiesInfo,
        } = this.props;

        if (!category?.length) {
            return null;
        }

        return category.map((categoryProperty) => {
            const currentStateKey = `${categoryProperty.priority}_${categoryProperty.name}`;
            const categorySectionAvailable = isCategorySectionAvailable(categoryProperty, products);

            if (!categorySectionAvailable) {
                return null;
            }

            return (
                <DesktopCompareResults
                    key={categoryProperty.name}
                    category={categoryProperty}
                    products={products}
                    currentStateKey={currentStateKey}
                    toggleSectionHandler={toggleSectionHandler}
                    toggleState={toggleSectionState[currentStateKey]}
                    showDevPrioritiesInfo={showDevPrioritiesInfo}
                    isKeyCategory={categoryProperty.keySection}
                    hideSimilarFeatures={hideSimilarFeatures}
                    highlightDifferences={highlightDifferences}
                    isHorizontalScroll={this.state.isHorizontalScroll}
                    runtimeInfo={this.props.runtimeInfo}
                    linkClickHandlerCallback={this.props.linkClickHandlerCallback}
                />
            );
        });
    }

    render() {
        const { categories, keyCategories } = this.props;

        const categoryList = [keyCategories, categories];

        return (
            <DesktopViewContext.Provider value={{ ...this.state }}>
                {this.renderProducts(false) /* regular comparison items */}
                <div
                    ref={this.stickyContainer}
                    className={styles.stickyContainer}
                >
                    {this.renderProducts(true) /* minified comparison items */}
                    <div
                        ref={this.compareResultsContainer}
                        className={styles.compareResultsContainer}
                    >
                        {categoryList.map((category) =>
                            this.renderCompareResults(category),
                        )}
                    </div>
                </div>
                <div ref={this.scroller} className={styles.scroller}>
                    <div ref={this.scrollerContent}></div>
                </div>
            </DesktopViewContext.Provider>
        );
    }
}

export default injectIntl(withNotificationStorageContext(DesktopProducts));
